import { FC, useEffect, useMemo } from 'react';

import * as yup from 'yup';
import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import { useFormik } from 'formik';
import { DISPLAY_DATE_FORMAT, mapServerError } from 'core';
import { Exam } from 'store/my-registrations/types';
import { ReadonlyField, ServerErrors } from 'components';
import { useAppSelector } from 'store';
import {
  useCancelRegistrationMutation,
  useGetCancellationReasonsQuery,
} from 'store/my-registrations/myRegistrationsAPI';
import { selectSystemDateTime } from 'store/features/featuresSlice';

import { validationRules } from '@britishcouncil/react-forms/';
import { useFormFields, useServerErrors } from '@britishcouncil/react-forms';
import { Modal, SelectField, TextArea } from '@britishcouncil/react-solas-ors3';
import { Button } from '@britishcouncil/react-solas';

import { useMyRegistrationsData } from '../useMyRegistrationsData';

export const ExamCancelModal: FC<{ exam: Exam; onExit: () => void }> = ({ exam, onExit }) => {
  const { data: cancellationReasons, isFetching: cancellationReasonsLoading } = useGetCancellationReasonsQuery();
  const systemDateTime = useAppSelector(selectSystemDateTime);
  const { refreshCandidateSummary, user } = useMyRegistrationsData();
  const [cancelRegistration, { isLoading: submitting, error, isSuccess: submitted, isError: isSubmitError }] =
    useCancelRegistrationMutation();

  const serverErrors = useMemo(() => (isSubmitError ? mapServerError(error) : []), [error, isSubmitError]);

  const formik = useFormik({
    initialValues: {
      cancellationReasonId: '',
      cancellationInfo: '',
    },
    validationSchema: yup.object().shape({
      cancellationReasonId: yup.string().required(validationRules.requiredMessage),
      cancellationInfo: yup.string().required(validationRules.requiredMessage).max(1000, 'Max length is 1000'),
    }),
    onSubmit: (values) => {
      cancelRegistration({
        ...values,
        cancellationReasonId: Number(values.cancellationReasonId),
        registrationId: exam.id,
      });
    },
  });

  const fields = useFormFields(formik);
  const mainErrors = useServerErrors(formik, serverErrors);

  useEffect(() => {
    if (submitted) {
      refreshCandidateSummary();
      onExit();
    }
  }, [onExit, refreshCandidateSummary, submitted]);

  return (
    <Modal
      show={true}
      title="Exam cancellation"
      onExit={onExit}
      isLoading={cancellationReasonsLoading}
      footer={
        <Footer>
          <Button intent="default" onClick={onExit} disabled={submitting}>
            Cancel
          </Button>
          <Button intent="primary" onClick={formik.submitForm} disabled={submitting} loading={submitting}>
            Save
          </Button>
        </Footer>
      }
    >
      <Container>
        <SelectField label="Cancellation reason" {...fields.cancellationReasonId} options={cancellationReasons || []} />
        <TextArea label="Cancellation info" placeholder="Cancellation info" {...fields.cancellationInfo} />
        <ReadonlyField label="Cancellation date">
          {DateTime.fromMillis(systemDateTime).toFormat(DISPLAY_DATE_FORMAT)}
        </ReadonlyField>
        <ReadonlyField label="Cancelled by">{user?.email}</ReadonlyField>
        {!!mainErrors && <ServerErrors errors={mainErrors} />}
      </Container>
    </Modal>
  );
};

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Container = styled.div`
  max-width: 632px;

  @media (min-width: 850px) {
    width: 632px;
  }
`;
